import { useCallback } from 'react'
import { useAttributes } from '@aytm/app/insights/dashboard/components/hooks'
import type { GroupListModel } from '@aytm/app/insights/dashboard/models/types.ClientModel'
import { customNaturalComparer } from '@aytm/app/insights/dashboard/lib/strings'
import { isReservedSectionName } from '@aytm/app/insights/dashboard/models/BlockModel.elements'
import type { AttributeType, TargetType } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import usePool from '@duckness/use-pool'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { stateSelector, shouldUpdate, type StateSelectorParams } from './SectionToggle.selectors'
import { poolDispatch } from '@aytm/app/insights/dashboard/duckness/poolDispatch'
import {
  makeApplyMetricsToSectionAction,
  makeAttributeAddEventAction,
  makeGroupAddEventAction,
  makeSectionAddEventAction,
  makeSectionDeleteEventAction,
  makeSectionRenameEventAction,
  makeSectionReplaceEventAction
} from '@aytm/app/insights/dashboard/duckness/ducks/EventDuck/EventDuck.actionCreators'

// export type TargetType = 'attribute' | 'entity' | 'meta'
type CreateNewSectionHandler = {
  (sectionName: string, target: TargetType, referenceSectionName?: string): void
}

type SectionRenameHandler = {
  (sectionName: string, newSectionName: string): void
}

type SectionDeleteHandler = {
  (sectionName: string, target: TargetType): void
}

type AddAttributeHandler = {
  (sectionName: string, attribute: string, target: TargetType): void
}

type AddGroupHandler = {
  (sectionName: string, groupName: string, target: TargetType): void
}

export type SectionNameValidator = {
  (sectionName: string, target: TargetType): [boolean, string?]
}

type AttributesForSectionGetter = {
  (sectionName: string, target: TargetType): string[]
}

type SectionHasItemsChecker = {
  (sectionName: string, target: TargetType): boolean
}

type SectionReplaceHandler = {
  (stationarySectionName: string, droppedSectionName: string): void
}

type ApplyMetricsToSectionHandler = {
  (sectionName: string, target: TargetType, metricsId: string): void
}

type UseSectionsReturnType = {
  groups: GroupListModel | null
  getAvailableAttributesForSection: AttributesForSectionGetter
  onCreateNewSection: CreateNewSectionHandler
  onDeleteSection: SectionDeleteHandler
  onRenameSection: SectionRenameHandler
  onAddAttribute: AddAttributeHandler
  onAddGroup: AddGroupHandler
  validateSectionName: SectionNameValidator
  hasSectionItems: SectionHasItemsChecker
  getCurrentSectionItems: AttributesForSectionGetter
  onReplaceSection: SectionReplaceHandler
  onApplyMetricsToSection: ApplyMetricsToSectionHandler
  attributes: Record<string, AttributeType>
}

// TODO: Optimize after implementation
export const useSections = (): UseSectionsReturnType => {
  const { attributes } = useAttributes()

  const { groups, sections } = usePool<typeof InsightsDashboardPool, StateSelectorParams, InsightsDashboardPoolState>(
    InsightsDashboardPool,
    stateSelector,
    shouldUpdate
  )

  const hasSectionItems = useCallback<SectionHasItemsChecker>(
    (sectionName, target) => {
      const existingSection = sections.find(g => g.name === sectionName && g.target === target)
      return existingSection && existingSection?.items?.size > 0
    },
    [sections]
  )

  const getCurrentSectionItems = useCallback<AttributesForSectionGetter>(
    (sectionName, target) => {
      const existingSection = sections.find(g => g.name === sectionName && g.target === target)
      return existingSection?.items?.toJS() ?? []
    },
    [sections]
  )

  const getAvailableAttributesForSection = useCallback<AttributesForSectionGetter>(
    (sectionName, target) => {
      const existingSection = sections.find(g => g.name === sectionName && g.target === target)
      if (!existingSection) {
        return [] as string[]
      }
      const result = Object.keys(attributes)
        .map(key => (attributes[key].target === target ? key : null))
        .filter(item => Boolean(item) && !existingSection.items?.includes?.(item))
      result.sort(customNaturalComparer)
      return result
    },
    [attributes, sections]
  )

  const validateSectionName = useCallback<SectionNameValidator>(
    (sectionName, target) => {
      if (!(Boolean(sectionName) && !/^\s+$/.test(sectionName))) {
        return [false, `Name is required`]
      }
      const nameCandidate = sectionName.trim()
      if (nameCandidate.length > 128) {
        return [false, 'The maximum length is 128 symbols']
      }
      if (isReservedSectionName(nameCandidate)) {
        return [false, `This name is taken or reserved`]
      }
      const existingSection = sections.find(g => g.name === nameCandidate && g.target === target)
      return existingSection ? [false, `This name is taken or reserved`] : [true]
    },
    [sections]
  )

  const onCreateNewSection = useCallback<CreateNewSectionHandler>((sectionName, target, referenceSectionName = '') => {
    poolDispatch(makeSectionAddEventAction(sectionName, target, referenceSectionName))
  }, [])

  const onRenameSection = useCallback<SectionRenameHandler>((sectionName, newSectionName) => {
    poolDispatch(makeSectionRenameEventAction(sectionName, newSectionName))
  }, [])

  const onDeleteSection = useCallback<SectionDeleteHandler>((sectionName, target) => {
    poolDispatch(makeSectionDeleteEventAction(sectionName, target))
  }, [])

  const onAddAttribute = useCallback<AddAttributeHandler>((sectionName, attribute, target) => {
    poolDispatch(makeAttributeAddEventAction(sectionName, attribute, target))
  }, [])

  const onAddGroup = useCallback<AddGroupHandler>((sectionName, groupName, target) => {
    poolDispatch(makeGroupAddEventAction(sectionName, groupName, target))
  }, [])

  const onReplaceSection = useCallback<SectionReplaceHandler>((stationarySectionName, droppedSectionName) => {
    poolDispatch(makeSectionReplaceEventAction(stationarySectionName, droppedSectionName))
  }, [])

  const onApplyMetricsToSection = useCallback<ApplyMetricsToSectionHandler>((sectionName, target, metricsId) => {
    poolDispatch(makeApplyMetricsToSectionAction(sectionName, target, metricsId))
  }, [])

  return {
    attributes,
    groups,
    getAvailableAttributesForSection,
    validateSectionName,
    onCreateNewSection,
    onRenameSection,
    onDeleteSection,
    onAddAttribute,
    onAddGroup,
    onReplaceSection,
    onApplyMetricsToSection,
    hasSectionItems,
    getCurrentSectionItems
  }
}
